import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

const CATEGORIES = ['general', 'contract', 'permit', 'coi', 'invoice', 'other']

function daysUntil(date) {
  if (!date) return null
  const diff = new Date(date) - new Date()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export default function DocumentsPage() {
  const { profile, activeBusiness } = useAuth()
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState('all')
  const [showUpload, setShowUpload] = useState(false)
  const role = profile?.role
  const canUpload = role === 'owner' || role === 'office_staff' || role === 'project_manager'
  const canDelete = role === 'owner' || role === 'office_staff'

  const fetchDocs = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('documents')
      .select('*, profiles:uploaded_by(full_name)')
      .order('created_at', { ascending: false })
    if (activeBusiness !== 'all') query = query.eq('business_id', activeBusiness)
    if (category !== 'all') query = query.eq('category', category)
    const { data, error } = await query
    if (error) console.error('Fetch docs error:', error.message)
    setDocs(data || [])
    setLoading(false)
  }, [activeBusiness, category])

  useEffect(() => { fetchDocs() }, [fetchDocs])

  const handleView = async (doc) => {
    if (!doc.file_path) return
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.file_path, 3600)
    if (error || !data?.signedUrl) {
      alert('Error generating download link: ' + (error?.message || 'Unknown error'))
      return
    }
    window.open(data.signedUrl, '_blank')
  }

  const handleDelete = async (doc) => {
    if (!window.confirm('Delete ' + doc.name + '?')) return
    if (doc.file_path) {
      await supabase.storage.from('documents').remove([doc.file_path])
    }
    await supabase.from('documents').delete().eq('id', doc.id)
    fetchDocs()
  }

  const expiringDocs = docs.filter(d => d.expiration_date && daysUntil(d.expiration_date) <= 30 && daysUntil(d.expiration_date) >= 0)

  return (
    <div style={styles.container}>
      {showUpload && (
        <UploadModal
          activeBusiness={activeBusiness}
          uploadedBy={profile?.id}
          onClose={() => setShowUpload(false)}
          onSave={() => { setShowUpload(false); fetchDocs() }}
        />
      )}

      <div style={styles.header}>
        <div>
          <div style={styles.title}>DOCUMENTS</div>
          <div style={styles.subtitle}>{docs.length} files</div>
        </div>
        {canUpload && (
          <button onClick={() => setShowUpload(true)} style={styles.uploadBtn}>+ UPLOAD</button>
        )}
      </div>

      {expiringDocs.length > 0 && (
        <div style={styles.alertBar}>
          <span style={styles.alertIcon}>{String.fromCodePoint(0x26A0, 0xFE0F)}</span>
          <span style={styles.alertText}>
            {expiringDocs.length} document{expiringDocs.length > 1 ? 's' : ''} expiring soon:{' '}
            {expiringDocs.map(d => d.name + ' (' + daysUntil(d.expiration_date) + ' days)').join(', ')}
          </span>
        </div>
      )}

      <div style={styles.filters}>
        {['all', ...CATEGORIES].map(c => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            style={{...styles.filterBtn, ...(category === c ? styles.filterBtnActive : {})}}
          >
            {c.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={styles.empty}>Loading documents...</div>
      ) : docs.length === 0 ? (
        <div style={styles.empty}>No documents found.</div>
      ) : (
        <div style={styles.docList}>
          {docs.map(doc => {
            const days = daysUntil(doc.expiration_date)
            const expiring = days !== null && days <= 30 && days >= 0
            const expired = days !== null && days < 0
            return (
              <div
                key={doc.id}
                style={{
                  ...styles.docRow,
                  ...(expiring ? styles.docRowExpiring : {}),
                  ...(expired ? styles.docRowExpired : {}),
                }}
              >
                <div style={styles.docIcon}>{getCategoryIcon(doc.category)}</div>
                <div style={styles.docInfo}>
                  <div style={styles.docName}>{doc.name}</div>
                  <div style={styles.docMeta}>
                    {doc.category?.toUpperCase()}
                    {' ' + String.fromCodePoint(0x00B7) + ' '}
                    {doc.profiles?.full_name || 'Unknown'}
                    {' ' + String.fromCodePoint(0x00B7) + ' '}
                    {new Date(doc.created_at).toLocaleDateString()}
                    {doc.expiration_date && (
                      <span style={{ color: expired ? '#e05c5c' : expiring ? '#b48c3c' : 'rgba(255,255,255,0.3)' }}>
                        {' ' + String.fromCodePoint(0x00B7) + ' Expires ' + doc.expiration_date}
                        {expiring ? ' (' + days + ' days)' : ''}
                        {expired ? ' (EXPIRED)' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div style={styles.docActions}>
                  <button onClick={() => handleView(doc)} style={styles.viewBtn}>VIEW</button>
                  {canDelete && (
                    <button onClick={() => handleDelete(doc)} style={styles.deleteBtn}>DELETE</button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function getCategoryIcon(cat) {
  const icons = {
    contract: String.fromCodePoint(0x1F4C4),
    permit: String.fromCodePoint(0x1F3DB, 0xFE0F),
    coi: String.fromCodePoint(0x1F6E1, 0xFE0F),
    invoice: String.fromCodePoint(0x1F4B5),
    general: String.fromCodePoint(0x1F4C1),
    other: String.fromCodePoint(0x1F50E),
  }
  return icons[cat] || String.fromCodePoint(0x1F4C1)
}

function UploadModal({ activeBusiness, uploadedBy, onClose, onSave }) {
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState({
    name: '',
    category: 'general',
    business_id: activeBusiness !== 'all' ? activeBusiness : 'pools',
    expiration_date: '',
    notes: '',
  })
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleUpload = async () => {
    if (!file || !form.name) {
      setError('Please select a file and enter a name.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be under 10MB.')
      return
    }
    setUploading(true)
    setError('')

    const ext = file.name.split('.').pop()
    const filePath = form.business_id + '/' + Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + ext

    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file)

    if (uploadError) {
      setError('Upload failed: ' + uploadError.message)
      setUploading(false)
      return
    }

    const { error: dbError } = await supabase.from('documents').insert({
      name: form.name,
      category: form.category,
      business_id: form.business_id,
      uploaded_by: uploadedBy,
      file_path: filePath,
      file_size: file.size,
      expiration_date: form.expiration_date || null,
      notes: form.notes || null,
    })

    if (dbError) {
      setError('Save failed: ' + dbError.message)
      await supabase.storage.from('documents').remove([filePath])
      setUploading(false)
      return
    }
    onSave()
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>UPLOAD DOCUMENT</div>
          <button onClick={onClose} style={styles.closeBtn}>{String.fromCodePoint(0x2715)}</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.field}>
            <label style={styles.label}>DOCUMENT NAME</label>
            <input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              style={styles.input}
              placeholder="e.g. Liability Insurance 2025"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>CATEGORY</label>
            <select value={form.category} onChange={e => set('category', e.target.value)} style={styles.input}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>BUSINESS</label>
            <select value={form.business_id} onChange={e => set('business_id', e.target.value)} style={styles.input}>
              <option value="pools">CP Quality Pools</option>
              <option value="riverbend">Riverbend Clearing</option>
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>EXPIRY DATE (optional {String.fromCodePoint(0x2014)} for COIs, permits)</label>
            <input
              value={form.expiration_date}
              onChange={e => set('expiration_date', e.target.value)}
              style={styles.input}
              type="date"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>NOTES (optional)</label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              style={{...styles.input, minHeight: '60px', resize: 'vertical'}}
              placeholder="Any additional details..."
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>FILE (max 10MB)</label>
            <input
              type="file"
              onChange={e => setFile(e.target.files[0])}
              style={{...styles.input, padding: '8px'}}
            />
          </div>
          {error && <div style={styles.errorMsg}>{error}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
          <button onClick={handleUpload} disabled={uploading} style={styles.saveBtn}>
            {uploading ? 'UPLOADING...' : 'UPLOAD'}
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  uploadBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 20px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  alertBar: { display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.25)', borderRadius: '4px', padding: '14px 16px', marginBottom: '20px' },
  alertIcon: { fontSize: '18px' },
  alertText: { fontSize: '14px', color: 'rgba(255,255,255,0.7)' },
  filters: { display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '20px' },
  filterBtn: { padding: '7px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  filterBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' },
  docList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  docRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  docRowExpiring: { border: '1px solid rgba(180,140,60,0.3)', backgroundColor: 'rgba(180,140,60,0.04)' },
  docRowExpired: { border: '1px solid rgba(224,92,92,0.3)', backgroundColor: 'rgba(224,92,92,0.04)' },
  docIcon: { fontSize: '24px', flexShrink: 0 },
  docInfo: { flex: 1, minWidth: 0 },
  docName: { fontSize: '16px', color: '#ffffff', marginBottom: '4px' },
  docMeta: { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },
  docActions: { display: 'flex', gap: '8px', flexShrink: 0 },
  viewBtn: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.6)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1px', cursor: 'pointer' },
  deleteBtn: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1px', cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { backgroundColor: '#0d1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', width: '100%', maxWidth: '520px', overflow: 'hidden', display: 'flex', flexDirection: 'column', fontFamily: "'Barlow',sans-serif" },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  modalTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '3px', color: '#b48c3c' },
  closeBtn: { backgroundColor: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' },
  modalBody: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' },
  input: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '10px 12px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow',sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
  errorMsg: { backgroundColor: 'rgba(220,60,60,0.1)', border: '1px solid rgba(220,60,60,0.3)', borderRadius: '3px', padding: '10px 14px', color: '#ff6b6b', fontSize: '14px' },
  modalFooter: { display: 'flex', gap: '12px', justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.06)' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '12px 20px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', letterSpacing: '2px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 24px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
}
